/**
 * Bloque "Parte de vehículo" — pegable en RutaDetail (admin) y MiRuta (montador).
 *
 * Muestra:
 *  - Si NO hay parte abierto para la ruta: botón "Iniciar jornada (abrir parte)"
 *  - Si hay parte abierto: tarjeta con info + botón "Finalizar jornada (cerrar parte)"
 *  - Si hay parte cerrado: badge "Parte cerrado · X km · sin/con incidencia"
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Truck, Loader2, Play, Square, AlertTriangle, Fuel, CheckCircle2, Gauge,
} from "lucide-react";
import { toast } from "sonner";
import { CerrarParteDialog, DanosPreviosBlock } from "@/pages/Mantenimiento";

export default function ParteVehiculoBlock({ rutaId, vehiculoId, montadores = [], onChange, compact = false }) {
  const [parte, setParte] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openSalida, setOpenSalida] = useState(false);
  const [openCierre, setOpenCierre] = useState(false);
  const [openCambio, setOpenCambio] = useState(false);  // Iter 76

  const load = async () => {
    if (!rutaId) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/flota/partes?ruta_id=${rutaId}&limit=1`);
      setParte((data.items || [])[0] || null);
    } catch { setParte(null); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [rutaId]);

  if (!rutaId) return null;

  if (loading) {
    return (
      <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-3 flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> <span className="text-xs text-zinc-500">Cargando parte de vehículo…</span>
      </div>
    );
  }

  // Sin parte → invitar a abrir
  if (!parte) {
    return (
      <>
        <div className={`bg-amber-500/10 border-2 border-amber-500/40 rounded-xl ${compact ? "p-3" : "p-4"}`}
          data-testid="parte-vehiculo-block">
          <div className="flex items-center gap-2 mb-2">
            <Truck className="w-5 h-5 text-amber-300" />
            <h3 className="font-bold text-amber-200 text-sm">Parte de vehículo</h3>
          </div>
          <div className="text-xs text-zinc-400 mb-3">
            Antes de salir hacia el primer trabajo, rellena el parte del vehículo (conductor, km salida, daños previos).
          </div>
          {!vehiculoId && (
            <div className="text-[11px] text-rose-300 mb-2">⚠ Esta ruta no tiene vehículo asignado. Asigna uno antes.</div>
          )}
          <Button onClick={() => setOpenSalida(true)} disabled={!vehiculoId} data-testid="btn-abrir-parte"
            className="w-full bg-amber-500 hover:bg-amber-600 text-zinc-900 font-bold">
            <Play className="w-4 h-4 mr-1" /> Iniciar jornada (abrir parte)
          </Button>
          {/* Iter 76 — botón cambiar vehículo (también disponible antes de abrir parte) */}
          <button type="button" onClick={() => setOpenCambio(true)}
            data-testid="btn-cambiar-vehiculo"
            className="w-full mt-2 text-[11px] text-amber-200/80 hover:text-amber-100 underline">
            Cambiar vehículo de la ruta
          </button>
        </div>
        {openSalida && <AbrirParteRutaDialog rutaId={rutaId} vehiculoId={vehiculoId} montadores={montadores}
          onClose={() => setOpenSalida(false)}
          onSaved={() => { setOpenSalida(false); load(); onChange && onChange(); }} />}
        {openCambio && <CambiarVehiculoDialog rutaId={rutaId} parteAbierto={null}
          onClose={() => setOpenCambio(false)}
          onSaved={() => { setOpenCambio(false); onChange && onChange(); }} />}
      </>
    );
  }

  // Parte abierto → mostrar info + botón cierre
  if (parte.estado === "abierto") {
    return (
      <>
        <div className={`bg-emerald-500/10 border-2 border-emerald-500/40 rounded-xl ${compact ? "p-3" : "p-4"}`}
          data-testid="parte-vehiculo-block">
          <div className="flex items-center gap-2 mb-2">
            <Truck className="w-5 h-5 text-emerald-300 animate-pulse" />
            <h3 className="font-bold text-emerald-200 text-sm">Jornada en curso · {parte.numero}</h3>
          </div>
          <div className="text-xs text-zinc-300 grid grid-cols-2 gap-1 mb-2">
            <div><b>Conductor:</b> {parte.conductor_nombre}</div>
            <div><b>Km salida:</b> {parte.km_salida}</div>
            <div className="col-span-2"><b>Salida:</b> {parte.fecha_salida?.slice(0, 16).replace("T", " ")}</div>
            {parte.danos_previos_tiene && (
              <div className="col-span-2 text-amber-300">⚠ Daños previos: {parte.danos_previos_descripcion}</div>
            )}
          </div>
          <Button onClick={() => setOpenCierre(true)} data-testid="btn-cerrar-parte"
            className="w-full bg-emerald-500 hover:bg-emerald-600 text-zinc-900 font-bold">
            <Square className="w-4 h-4 mr-1" /> Finalizar jornada (cerrar parte)
          </Button>
          {/* Iter 76 — durante la ruta el montador puede haber cambiado de vehículo (avería). */}
          <button type="button" onClick={() => setOpenCambio(true)}
            data-testid="btn-cambiar-vehiculo-en-curso"
            className="w-full mt-2 text-[11px] text-emerald-200/80 hover:text-emerald-100 underline">
            Cambiar vehículo (avería / sustitución)
          </button>
        </div>
        {openCierre && <CerrarParteDialog parte={parte} onClose={() => setOpenCierre(false)}
          onSaved={() => { setOpenCierre(false); load(); onChange && onChange(); }} />}
        {openCambio && <CambiarVehiculoDialog rutaId={rutaId} parteAbierto={parte}
          onClose={() => setOpenCambio(false)}
          onSaved={() => { setOpenCambio(false); load(); onChange && onChange(); }} />}
      </>
    );
  }

  // Cerrado → resumen
  return (
    <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-3" data-testid="parte-vehiculo-block">
      <div className="flex items-center gap-2 mb-2">
        <CheckCircle2 className="w-4 h-4 text-emerald-400" />
        <h3 className="font-semibold text-sm">Parte cerrado · {parte.numero}</h3>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
        <Stat icon={Gauge} label="Recorridos" value={`${parte.km_recorridos ?? 0} km`} />
        <Stat icon={Gauge} label="Salida → Entrada" value={`${parte.km_salida} → ${parte.km_entrada}`} />
        {parte.repostaje_hizo && (
          <>
            <Stat icon={Fuel} label="Repostaje" value={`${parte.repostaje_litros}L · ${parte.repostaje_importe}€`} color="emerald" />
            {parte.consumo_l100 && <Stat icon={Fuel} label="Consumo" value={`${parte.consumo_l100} L/100km`} color="emerald" />}
          </>
        )}
        {parte.incidencia_tuvo && (
          <Stat icon={AlertTriangle} label="⚠ INCIDENCIA" value={(parte.incidencia_descripcion || "").slice(0, 30) + "..."} color="rose" />
        )}
      </div>
    </div>
  );
}

function Stat({ icon: Ico, label, value, color }) {
  const c = color === "emerald" ? "text-emerald-300" : color === "rose" ? "text-rose-300" : "text-zinc-300";
  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5">
      <div className="text-[9px] uppercase tracking-wider text-zinc-500 flex items-center gap-1">
        <Ico className="w-2.5 h-2.5" /> {label}
      </div>
      <div className={`text-xs font-bold ${c}`}>{value}</div>
    </div>
  );
}

function AbrirParteRutaDialog({ rutaId, vehiculoId: vehiculoIdInicial, montadores, onClose, onSaved }) {
  const [conductores, setConductores] = useState([]);
  const [conductor, setConductor] = useState("");
  const [vehiculoInfo, setVehiculoInfo] = useState(null);
  // Iter 76 — selector de vehículo modificable in-situ
  const [vehiculosFlota, setVehiculosFlota] = useState([]);
  const [vehiculoSelOrigen, setVehiculoSelOrigen] = useState("flota"); // flota | propio | otro
  const [vehiculoSelId, setVehiculoSelId] = useState(vehiculoIdInicial || "");
  const [vehiculoOtroLabel, setVehiculoOtroLabel] = useState("");
  const [motivoCambio, setMotivoCambio] = useState("");
  const [form, setForm] = useState({ km_salida: "", danos_previos_tiene: false, danos_previos_descripcion: "", danos_previos_fotos: [] });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/flota/conductores").then((r) => {
      const allCond = r.data.items || [];
      if (montadores && montadores.length > 0) {
        const ids = new Set(montadores.map((m) => m.user_id || m.colaborador_externo_id));
        const filtered = allCond.filter((c) => ids.has(c.id_user) || ids.has(c.id_colaborador));
        setConductores(filtered.length > 0 ? filtered : allCond);
        if (filtered.length === 1) {
          const u = filtered[0];
          setConductor(`${u.tipo}|${u.id_user || u.id_colaborador}`);
        }
      } else {
        setConductores(allCond);
      }
    });
    // Iter 76 — cargar flota activa
    api.get("/vehiculos").then((r) => {
      const items = Array.isArray(r.data) ? r.data : (r.data.items || []);
      setVehiculosFlota(items.filter((v) => v.activo !== false));
    }).catch(() => setVehiculosFlota([]));
    if (vehiculoIdInicial) {
      api.get(`/vehiculos/${vehiculoIdInicial}`).then((r) => setVehiculoInfo(r.data)).catch(() => {});
    }
  }, [vehiculoIdInicial, montadores]);

  // Refrescar info del vehículo cuando cambia la selección
  useEffect(() => {
    if (vehiculoSelOrigen === "flota" && vehiculoSelId) {
      api.get(`/vehiculos/${vehiculoSelId}`).then((r) => setVehiculoInfo(r.data)).catch(() => setVehiculoInfo(null));
    } else {
      setVehiculoInfo(null);
    }
  }, [vehiculoSelOrigen, vehiculoSelId]);

  const cambioVehiculo = (
    vehiculoSelOrigen !== "flota"
    || (vehiculoSelId && vehiculoSelId !== vehiculoIdInicial)
  );

  const submit = async () => {
    if (!conductor) return toast.error("Selecciona conductor");
    if (vehiculoSelOrigen === "flota" && !vehiculoSelId) return toast.error("Selecciona vehículo");
    if (vehiculoSelOrigen === "otro" && !vehiculoOtroLabel.trim()) return toast.error("Indica el vehículo");
    if (vehiculoSelOrigen === "flota" && !form.km_salida) return toast.error("Indica km de salida");
    setSaving(true);
    try {
      // Iter 76 — si el montador cambió el vehículo, primero actualizar la ruta
      if (cambioVehiculo) {
        const body = {};
        if (vehiculoSelOrigen === "flota") body.vehiculo_id = vehiculoSelId;
        else if (vehiculoSelOrigen === "propio") body.vehiculo_propio = true;
        else body.vehiculo_otro_label = vehiculoOtroLabel.trim();
        if (motivoCambio.trim()) body.motivo = motivoCambio.trim();
        await api.patch(`/rutas/${rutaId}/vehiculo`, body);
      }

      // Si NO es vehículo de flota, no se abre parte de vehículo (propio/otro no llevan parte)
      if (vehiculoSelOrigen !== "flota") {
        toast.success("Vehículo de la ruta actualizado");
        onSaved();
        return;
      }

      const [tipo, idVal] = conductor.split("|");
      const body = {
        vehiculo_id: vehiculoSelId,
        km_salida: parseFloat(form.km_salida),
        uso: "ruta",
        ruta_id: rutaId,
        danos_previos_tiene: form.danos_previos_tiene,
        danos_previos_descripcion: form.danos_previos_tiene ? form.danos_previos_descripcion : null,
        danos_previos_fotos: form.danos_previos_tiene ? form.danos_previos_fotos : [],
      };
      if (tipo === "interno") body.conductor_user_id = idVal;
      else body.conductor_colaborador_id = idVal;
      await api.post("/flota/partes/abrir", body);
      toast.success("Parte abierto · jornada iniciada");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error abriendo parte");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end md:items-center justify-center p-0 md:p-4" onClick={onClose}>
      <div className="bg-zinc-950 border border-zinc-800 rounded-t-xl md:rounded-xl w-full md:max-w-2xl max-h-[95vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()} data-testid="abrir-parte-dialog">
        <div className="sticky top-0 bg-zinc-950 border-b border-zinc-800 p-3 flex items-center justify-between">
          <h2 className="text-lg font-bold flex items-center gap-2"><Play className="w-5 h-5 text-amber-400" /> Iniciar jornada</h2>
          <button onClick={onClose} className="text-zinc-500">✕</button>
        </div>
        <div className="p-4 space-y-3">
          {/* Iter 76 — Selector de vehículo modificable in-situ */}
          <div className="bg-amber-500/5 border border-amber-500/30 rounded-lg p-3 space-y-2"
               data-testid="vehiculo-selector-block">
            <div className="flex items-center justify-between gap-2">
              <label className="text-[10px] uppercase tracking-wider text-amber-300 font-bold">
                Vehículo de la ruta
              </label>
              {cambioVehiculo && (
                <span className="text-[10px] bg-amber-500 text-zinc-900 font-bold px-1.5 py-0.5 rounded">
                  CAMBIO
                </span>
              )}
            </div>
            <div className="flex gap-1 text-[11px]">
              {[
                { v: "flota", l: "Flota DNF" },
                { v: "propio", l: "Vehículo propio" },
                { v: "otro", l: "Otro / alquiler" },
              ].map((opt) => (
                <button key={opt.v} type="button"
                  data-testid={`vehiculo-tipo-${opt.v}`}
                  onClick={() => setVehiculoSelOrigen(opt.v)}
                  className={`flex-1 px-2 py-1 rounded border ${vehiculoSelOrigen === opt.v
                    ? "bg-amber-500 text-zinc-900 border-amber-500 font-bold"
                    : "bg-zinc-950 text-zinc-300 border-zinc-800 hover:border-amber-500/50"}`}>
                  {opt.l}
                </button>
              ))}
            </div>
            {vehiculoSelOrigen === "flota" && (
              <select value={vehiculoSelId} onChange={(e) => setVehiculoSelId(e.target.value)}
                data-testid="vehiculo-select-flota"
                className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-sm">
                <option value="">— Selecciona vehículo —</option>
                {vehiculosFlota.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.nombre} {v.matricula ? `· ${v.matricula}` : ""} {v.km_actuales ? `· ${v.km_actuales} km` : ""}
                  </option>
                ))}
              </select>
            )}
            {vehiculoSelOrigen === "propio" && (
              <div className="text-[11px] text-amber-200 bg-amber-500/10 rounded p-1.5">
                El montador usará su propio vehículo. No se abrirá parte de vehículo de flota.
              </div>
            )}
            {vehiculoSelOrigen === "otro" && (
              <input type="text" value={vehiculoOtroLabel}
                onChange={(e) => setVehiculoOtroLabel(e.target.value)}
                data-testid="vehiculo-otro-label"
                placeholder="Ej: Furgo alquilada Europcar 1234ABC"
                maxLength={60}
                className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-sm" />
            )}
            {cambioVehiculo && (
              <input type="text" value={motivoCambio}
                onChange={(e) => setMotivoCambio(e.target.value)}
                data-testid="vehiculo-motivo"
                placeholder="Motivo del cambio (opcional, ej. avería del asignado)"
                maxLength={200}
                className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-[11px]" />
            )}
          </div>

          {vehiculoInfo && vehiculoSelOrigen === "flota" && (
            <div className="bg-zinc-900/60 rounded p-2 text-xs text-zinc-400">
              <b className="text-zinc-200">{vehiculoInfo.nombre}</b> ({vehiculoInfo.matricula}) · Km actuales: <b>{vehiculoInfo.km_actuales ?? "—"}</b>
            </div>
          )}
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">
              Conductor* ({conductores.length} con carnet de conducir)
            </label>
            <select value={conductor} onChange={(e) => setConductor(e.target.value)} data-testid="abrir-conductor"
              className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-sm">
              <option value="">— Selecciona —</option>
              {conductores.map((c) => (
                <option key={`${c.tipo}-${c.id_user || c.id_colaborador}`} value={`${c.tipo}|${c.id_user || c.id_colaborador}`}>
                  {c.tipo === "interno" ? "👤" : "🤝"} {c.nombre} · carnet {c.carnet_tipo}
                </option>
              ))}
            </select>
            {conductores.length === 0 && (
              <div className="text-[10px] text-rose-400 mt-1">
                ⚠ Ningún montador asignado a la ruta tiene carnet de conducir registrado. Añádelo en Configuración → Usuarios o Colaboradores.
              </div>
            )}
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Km salida{vehiculoSelOrigen === "flota" ? "*" : " (opcional)"}</label>
            <input type="number" value={form.km_salida} onChange={(e) => setForm({ ...form, km_salida: e.target.value })}
              data-testid="abrir-km" min={vehiculoInfo?.km_actuales || 0}
              disabled={vehiculoSelOrigen !== "flota"}
              className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-sm disabled:opacity-40" />
          </div>
          <DanosPreviosBlock value={form} onChange={(v) => setForm({ ...form, ...v })} />
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose}>Cancelar</Button>
            <Button onClick={submit} disabled={saving} data-testid="btn-confirmar-salida"
              className="bg-amber-500 hover:bg-amber-600 text-zinc-900 font-bold">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Play className="w-4 h-4 mr-1" /> Iniciar jornada</>}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}



/**
 * Iter 76 — Dialog para cambiar el vehículo de la ruta in-situ.
 *
 * Casos:
 *  - parteAbierto = null → solo PATCH /rutas/{rid}/vehiculo
 *  - parteAbierto != null → avisa al user de que primero debe cerrar el parte
 *    actual (con km regreso del vehículo anterior). Tras el cierre, se permite
 *    re-abrir uno nuevo desde la pantalla principal.
 */
function CambiarVehiculoDialog({ rutaId, parteAbierto, onClose, onSaved }) {
  const [vehiculosFlota, setVehiculosFlota] = useState([]);
  const [origen, setOrigen] = useState("flota"); // flota | propio | otro
  const [vehId, setVehId] = useState("");
  const [otroLabel, setOtroLabel] = useState("");
  const [motivo, setMotivo] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/vehiculos").then((r) => {
      const items = Array.isArray(r.data) ? r.data : (r.data.items || []);
      setVehiculosFlota(items.filter((v) => v.activo !== false));
    }).catch(() => setVehiculosFlota([]));
  }, []);

  const submit = async () => {
    if (origen === "flota" && !vehId) return toast.error("Selecciona vehículo");
    if (origen === "otro" && !otroLabel.trim()) return toast.error("Indica el vehículo");
    setSaving(true);
    try {
      const body = {};
      if (origen === "flota") body.vehiculo_id = vehId;
      else if (origen === "propio") body.vehiculo_propio = true;
      else body.vehiculo_otro_label = otroLabel.trim();
      if (motivo.trim()) body.motivo = motivo.trim();
      const { data } = await api.patch(`/rutas/${rutaId}/vehiculo`, body);
      if (data.warning) {
        toast.warning(data.warning, { duration: 7000 });
      } else {
        toast.success(`Vehículo cambiado a: ${data.nuevo}`);
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cambiando vehículo");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end md:items-center justify-center p-0 md:p-4" onClick={onClose}>
      <div className="bg-zinc-950 border border-amber-500/40 rounded-t-xl md:rounded-xl w-full md:max-w-md max-h-[95vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()} data-testid="cambiar-vehiculo-dialog">
        <div className="sticky top-0 bg-zinc-950 border-b border-zinc-800 p-3 flex items-center justify-between">
          <h2 className="text-lg font-bold flex items-center gap-2 text-amber-200">
            <Truck className="w-5 h-5" /> Cambiar vehículo
          </h2>
          <button onClick={onClose} className="text-zinc-500">✕</button>
        </div>
        <div className="p-4 space-y-3">
          {parteAbierto && (
            <div className="rounded-md bg-rose-500/10 border border-rose-500/40 p-3 text-xs text-rose-200">
              <div className="font-bold mb-1">⚠ Hay un parte de vehículo en curso</div>
              <div>
                Parte <b>{parteAbierto.numero}</b> · km salida {parteAbierto.km_salida}.
                Después de cambiar el vehículo, cierra primero el parte actual con los km de regreso del
                vehículo anterior, y luego abre uno nuevo para el vehículo nuevo.
              </div>
            </div>
          )}
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Nuevo vehículo</label>
            <div className="flex gap-1 text-[11px] mb-2">
              {[
                { v: "flota", l: "Flota DNF" },
                { v: "propio", l: "Vehículo propio" },
                { v: "otro", l: "Otro / alquiler" },
              ].map((opt) => (
                <button key={opt.v} type="button"
                  data-testid={`cv-tipo-${opt.v}`}
                  onClick={() => setOrigen(opt.v)}
                  className={`flex-1 px-2 py-1 rounded border ${origen === opt.v
                    ? "bg-amber-500 text-zinc-900 border-amber-500 font-bold"
                    : "bg-zinc-950 text-zinc-300 border-zinc-800 hover:border-amber-500/50"}`}>
                  {opt.l}
                </button>
              ))}
            </div>
            {origen === "flota" && (
              <select value={vehId} onChange={(e) => setVehId(e.target.value)}
                data-testid="cv-select-flota"
                className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-sm">
                <option value="">— Selecciona vehículo —</option>
                {vehiculosFlota.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.nombre} {v.matricula ? `· ${v.matricula}` : ""}
                  </option>
                ))}
              </select>
            )}
            {origen === "otro" && (
              <input type="text" value={otroLabel}
                onChange={(e) => setOtroLabel(e.target.value)}
                data-testid="cv-otro-label"
                placeholder="Ej: Furgo alquilada Europcar 1234ABC"
                maxLength={60}
                className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-sm" />
            )}
          </div>
          <input type="text" value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            data-testid="cv-motivo"
            placeholder="Motivo (opcional): avería, sustitución, …"
            maxLength={200}
            className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-xs" />
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose}>Cancelar</Button>
            <Button onClick={submit} disabled={saving} data-testid="cv-confirmar"
              className="bg-amber-500 hover:bg-amber-600 text-zinc-900 font-bold">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirmar cambio"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
